import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { FaCheckCircle, FaTimesCircle } from "react-icons/fa";
import { useProgressContext } from "../../ContextProvider/ProgressContext";
import { jsPDF } from "jspdf";
import logo from '../../Assets/Images/uojlogo.png';
import uojApi from '../../Common/Api/uojApi';

export default function Results() {
  const navigate = useNavigate();
  const location = useLocation();
  const { setActiveState } = useProgressContext();

  const queryParams = new URLSearchParams(location.search);
  const transactionId = queryParams.get("transaction_id");

  const [paymentData, setPaymentData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const isSuccess = ["ACCEPT", "SUCCESS"].includes(paymentData?.transaction?.decision);

  useEffect(() => {
    setActiveState(3, isSuccess ? "success" : "failed");
  }, [setActiveState, isSuccess]);

  useEffect(() => {
    const fetchPaymentDetails = async () => {
      if (!transactionId) {
        setError("No transaction ID provided");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError("");
        
        const response = await uojApi.post('/payment-check', {
          transaction_id: transactionId
        });

        if (response.data?.http_status === 200 && response.data?.data) {
          setPaymentData(response.data.data);
        } else {
          setError("Payment details not found or invalid response");
        }
      } catch (error) {
        console.error('Payment check error:', error);
        
        // Handle different types of errors
        if (error.response?.status === 404) {
          setError("Payment record not found");
        } else if (error.response?.status === 500) {
          setError("Server error. Please try again later");
        } else if (error.response?.data?.message) {
          setError(error.response.data.message);
        } else {
          setError("Failed to fetch payment details. Please check your connection");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchPaymentDetails();
  }, [transactionId]);

  const handleButtonClick = () => {
    setActiveState(1);
    navigate("/");
  };

  const icon = isSuccess ? (
    <FaCheckCircle className="text-green-500 text-6xl" />
  ) : (
    <FaTimesCircle className="text-red-500 text-6xl" />
  );

  const formatDateTime = (dateTimeString) => {
    if (!dateTimeString) return "N/A";
    
    try {
      const date = new Date(dateTimeString);
      const formattedDate = date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
      const formattedTime = date.toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
      });
      return `${formattedDate} ${formattedTime}`;
    } catch (error) {
      console.error('Date formatting error:', error);
      return "N/A";
    }
  };

  const formatPaymentMethod = (method) => {
    if (!method) return "N/A";
    
    // Capitalize first letter and format
    return method.charAt(0).toUpperCase() + method.slice(1).toLowerCase();
  };

  const formatPaymentStatus = (decision) => {
    if (!decision) return "N/A";
    
    return (decision === "ACCEPT" || decision === "SUCCESS") ? "Accepted" : "Failed";
  };

  const generatePDF = () => {
    try {
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.width;
      const pageHeight = doc.internal.pageSize.height;

      // Add logo to the center of the page
      const logoWidth = 150;
      const logoHeight = 40;
      const logoX = (pageWidth - logoWidth) / 2;
      const logoY = 20;
      doc.addImage(logo, 'PNG', logoX, logoY, logoWidth, logoHeight);

      // Add title below the logo, centered
      doc.setFontSize(18);
      doc.setFont('helvetica', 'bold');
      const title = 'Payment Receipt';
      const titleWidth = doc.getStringUnitWidth(title) * doc.internal.getFontSize() / doc.internal.scaleFactor;
      const titleX = (pageWidth - titleWidth) / 2;
      const titleY = logoY + logoHeight + 20;
      doc.text(title, titleX, titleY);

      // Add a horizontal line separator
      doc.setDrawColor(0);
      doc.setLineWidth(0.2);
      const lineY = titleY + 10;
      doc.line(20, lineY, pageWidth - 20, lineY);

      // Set content font
      doc.setFontSize(12);
      doc.setFont('helvetica', 'normal');
      let yOffset = lineY + 15;

      // Add content dynamically using API data
      const content = [
        { label: 'Transaction ID', value: String(paymentData?.transaction?.id || "N/A") },
        { label: 'Voucher Code', value: String(paymentData?.order?.voucher_code || "N/A") },
        { label: 'Amount', value: `Rs. ${String(paymentData?.transaction?.amount || "N/A")}` },
        { label: 'Payment Method', value: formatPaymentMethod(paymentData?.transaction?.payment_method) },
        { label: 'Payment Status', value: formatPaymentStatus(paymentData?.transaction?.decision) },
        { label: 'Payee Name', value: String(paymentData?.order?.payee_name || "N/A") },
        { label: 'User Name', value: String(paymentData?.order?.user?.name || "N/A") },
        { label: 'Program Name', value: String(paymentData?.order?.program?.name || "N/A") },
        { label: 'Identity Type', value: String(paymentData?.order?.identity_type || "N/A") },
        { label: 'Identity Number', value: String(paymentData?.order?.identity_number || "N/A") },
        { label: 'Phone Number', value: String(paymentData?.order?.phone_number || "N/A") },
        { label: 'Payment Date', value: formatDateTime(paymentData?.transaction?.signed_date_time) },
      ];

      // Add each content line
      content.forEach((line) => {
        // Check if we need a new page
        if (yOffset > pageHeight - 40) {
          doc.addPage();
          yOffset = 30;
        }

        doc.setFont('helvetica', 'bold');
        doc.text(`${line.label}:`, 20, yOffset);
        doc.setFont('helvetica', 'normal');
        
        // Handle long text wrapping
        const textWidth = pageWidth - 90;
        const splitValue = doc.splitTextToSize(line.value, textWidth);
        doc.text(splitValue, 70, yOffset);
        
        yOffset += splitValue.length > 1 ? splitValue.length * 6 : 10;
      });

      // Add footer separator
      doc.setDrawColor(0);
      doc.setLineWidth(0.2);
      doc.line(20, yOffset + 5, pageWidth - 20, yOffset + 5);

      // Add footer
      doc.setFontSize(10);
      doc.setTextColor(100);
      doc.text("Thank you for your payment.", 20, yOffset + 15);
      doc.text("University of Jaffna - Official Receipt", 20, yOffset + 25);

      // Add page border
      doc.setDrawColor(200);
      doc.setLineWidth(0.5);
      doc.rect(10, 10, pageWidth - 20, pageHeight - 20);

      // Save with transaction ID in filename
      const fileName = `payment_receipt_${transactionId || 'unknown'}.pdf`;
      doc.save(fileName);
    } catch (error) {
      console.error('PDF generation error:', error);
      setError("Failed to generate PDF. Please try again.");
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center my-10">
        <div className="bg-white p-8 rounded-lg shadow-lg text-center max-w-md w-full">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <div className="text-blue-500 text-xl">Loading payment details...</div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-center my-10">
        <div className="bg-white p-8 rounded-lg shadow-lg text-center max-w-lg w-full mx-4">
          <div className="flex justify-center">{icon}</div>
          <h1 className="text-2xl font-bold mt-4">
            {isSuccess ? "Payment Successful!" : "Payment Failed!"}
          </h1>
          <p className="text-gray-600 mt-2">
            {isSuccess
              ? "Your payment has been processed successfully."
              : "Your payment could not be processed. Please try again."}
          </p>
          
          {/* Error Display */}
          {error && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg">
              <div className="flex items-center">
                <FaTimesCircle className="mr-2" />
                <span>{error}</span>
              </div>
            </div>
          )}

          {/* Payment Details */}
          {paymentData && (
            <div className="mt-6 bg-gray-50 p-4 rounded-lg">
              <h3 className="font-semibold text-gray-800 mb-4 text-lg">Payment Details</h3>
              <div className="space-y-3 text-left">
                <div className="flex justify-between items-center py-1 border-b border-gray-200">
                  <span className="font-medium text-gray-600">Transaction ID:</span>
                  <span className="text-gray-800 font-mono">{paymentData?.transaction?.id || "N/A"}</span>
                </div>
                <div className="flex justify-between items-center py-1 border-b border-gray-200">
                  <span className="font-medium text-gray-600">Voucher Code:</span>
                  <span className="text-gray-800 font-mono">{paymentData?.order?.voucher_code || "N/A"}</span>
                </div>
                <div className="flex justify-between items-center py-1 border-b border-gray-200">
                  <span className="font-medium text-gray-600">Amount:</span>
                  <span className="text-green-600 font-bold text-lg">Rs. {paymentData?.transaction?.amount || "N/A"}</span>
                </div>
                <div className="flex justify-between items-center py-1 border-b border-gray-200">
                  <span className="font-medium text-gray-600">Payment Method:</span>
                  <span className="text-gray-800">{formatPaymentMethod(paymentData?.transaction?.payment_method)}</span>
                </div>
                <div className="flex justify-between items-center py-1 border-b border-gray-200">
                  <span className="font-medium text-gray-600">Payment Status:</span>
                  <span className={`font-bold ${isSuccess ? 'text-green-600' : 'text-red-600'}`}>
                    {formatPaymentStatus(paymentData?.transaction?.decision)}
                  </span>
                </div>
                <div className="flex justify-between items-center py-1 border-b border-gray-200">
                  <span className="font-medium text-gray-600">Payee Name:</span>
                  <span className="text-gray-800">{paymentData?.order?.payee_name || "N/A"}</span>
                </div>
                <div className="flex justify-between items-center py-1 border-b border-gray-200">
                  <span className="font-medium text-gray-600">User Name:</span>
                  <span className="text-gray-800">{paymentData?.order?.user?.name || "N/A"}</span>
                </div>
                <div className="flex justify-between items-center py-1 border-b border-gray-200">
                  <span className="font-medium text-gray-600">Program:</span>
                  <span className="text-gray-800">{paymentData?.order?.program?.name || "N/A"}</span>
                </div>
                <div className="flex justify-between items-center py-1 border-b border-gray-200">
                  <span className="font-medium text-gray-600">Identity Type:</span>
                  <span className="text-gray-800">{paymentData?.order?.identity_type || "N/A"}</span>
                </div>
                <div className="flex justify-between items-center py-1 border-b border-gray-200">
                  <span className="font-medium text-gray-600">Identity Number:</span>
                  <span className="text-gray-800 font-mono">{paymentData?.order?.identity_number || "N/A"}</span>
                </div>
                <div className="flex justify-between items-center py-1 border-b border-gray-200">
                  <span className="font-medium text-gray-600">Phone Number:</span>
                  <span className="text-gray-800 font-mono">{paymentData?.order?.phone_number || "N/A"}</span>
                </div>
                <div className="flex justify-between items-center py-1">
                  <span className="font-medium text-gray-600">Payment Date:</span>
                  <span className="text-gray-800">{formatDateTime(paymentData?.transaction?.signed_date_time)}</span>
                </div>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
            <button
              className={`${
                isSuccess
                  ? "bg-blue-500 hover:bg-blue-600"
                  : "bg-red-500 hover:bg-red-600"
              } text-white px-6 py-2 rounded-lg transition duration-300 font-medium`}
              onClick={handleButtonClick}
            >
              {isSuccess ? "Back to Home" : "Retry Payment"}
            </button>
            
            {isSuccess && paymentData && (
              <button
                className="bg-green-500 hover:bg-green-600 text-white px-6 py-2 rounded-lg transition duration-300 font-medium"
                onClick={generatePDF}
              >
                Download PDF
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}